/**
 * Formats an address object into a readable string
 * @param {Object} address - The address object containing address fields
 * @returns {string} - Formatted address string
 */
export const formatAddress = (address) => {
  if (!address) return "";

  const parts = [
    address.street,
    address.streetTwo,
    address.city,
    address.stateProvince,
    address.postalCode,
    address.country,
  ].filter(Boolean);

  return parts.join(", ");
};

export const leaderboardDataMap = {
  FIRST_UPDATED: "First Updated",
  FIRST_ENTERED: "First Entered",
  AUTO: "Automatic",
  MANUAL: "Manual",
  DESC: "Descending",
  ASC: "Ascending",
  ACCRUAL: "Accrual",
  HIGHEST: "Highest",
  LOWEST: "Lowest",
};

/**
 * Get access token from local storage
 * @returns {string} - The access token
 */
export const getAccessToken = () => {
  const tokenStorage = localStorage.getItem("okta-token-storage");
  return tokenStorage
    ? JSON.parse(tokenStorage)?.accessToken?.accessToken
    : localStorage.getItem("accessToken");
};

/**
 * Get program ID from global state or local storage
 * @param {Object} globalState - The global state object
 * @returns {string} - The program ID
 */
export const getProgramId = (globalState) => {
  return globalState?.programConfig?.id || localStorage.getItem("programId");
};

/**
 * Create a form data object for file upload with proper metadata formatting
 * @param {File} file - The file to upload
 * @param {string} leaderboardId - The ID of the leaderboard
 * @returns {FormData} - The form data object
 */
export const createFileUploadFormData = (file, leaderboardId) => {
  const formData = new FormData();
  formData.append("file", file);

  // Create a Blob for the metadata with leaderboardId
  const metadataJSON = JSON.stringify({ leaderboard_id: leaderboardId });
  const metadataBlob = new Blob([metadataJSON], { type: "application/json" });
  formData.append("metadata", metadataBlob);

  return formData;
};

/**
 * Generic file upload function with polling
 * @param {Object} config - Upload configuration
 * @param {File} config.file - The file to upload
 * @param {string} config.baseUrl - The base URL for the API
 * @param {string} config.accessToken - The access token
 * @param {string} config.programId - The program ID
 * @param {string} config.context - The context for the upload (e.g., CONSUMER_REWARD_STATUS_UPDATES)
 * @param {FormData} config.formData - Pre-constructed form data
 * @param {Object} config.additionalParams - Additional URL parameters
 * @param {number} [config.maxAttempts=30] - Maximum polling attempts
 * @param {number} [config.pollingInterval=2000] - Polling interval in milliseconds
 * @returns {Promise<Object>} - Promise resolving to the upload result
 */
export const uploadFileWithPolling = async (config) => {
  const {
    file,
    baseUrl,
    accessToken,
    programId,
    context,
    formData,
    additionalParams = {},
    maxAttempts = 30,
    pollingInterval = 2000,
  } = config;

  const { generateSHA256 } = await import("./generateFileSHA");
  const axios = (await import("axios")).default;

  // Generate SHA256 hash for the file
  const fileSha256 = await generateSHA256(file);

  // Construct the endpoint URL with parameters
  const params = new URLSearchParams({
    context,
    auto_process: "true",
    file_sha256: fileSha256,
    ...additionalParams,
  });

  const endpoint = `${baseUrl}import?${params.toString()}`;

  // Make the API call
  const response = await axios.post(endpoint, formData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "program-id": programId,
      "Content-Type": "multipart/form-data",
    },
  });

  if (response.status !== 202) {
    throw new Error("Upload failed");
  }

  let pollingId = response.data?.polling_id || response.data?.pollind_id;
  if (!pollingId) {
    return { status: 202, ready_to_download: true };
  }

  let isReady = false;
  let attempts = 0;

  while (!isReady && attempts < maxAttempts) {
    attempts++;

    // Wait before checking status
    await new Promise((resolve) => setTimeout(resolve, pollingInterval));

    // Check status
    const statusResponse = await axios.get(`${baseUrl}polling/${pollingId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "program-id": programId,
      },
    });

    const statusData = statusResponse.data;

    // Handle failed status
    if (statusData.status === "FAILED") {
      const errorMessage =
        statusData?.sanitized_message ||
        statusData?.error_message?.message ||
        "Upload failed";
      throw new Error(errorMessage);
    }

    // Check if file is ready to download
    if (statusData.ready_to_download) {
      isReady = true;
    }
  }

  if (isReady) {
    return { status: 202 };
  }

  // If we've reached max attempts without success
  throw new Error("Upload processing timed out");
};

export const leaderboardEditDataMap = (leaderboard) => {
  return {
    id: leaderboard.id,
    programId: leaderboard.programId,
    title: leaderboard.title,
    startsAt: leaderboard.startsAt,
    endsAt: leaderboard.endsAt,
    status: leaderboard.status,
    rankOrder: [
      {
        id: leaderboard.rankOrder,
        displayText: leaderboard.rankOrder === "ASC" ? "Ascending" : "Descending",
      },
    ],
    tieBreaker: [
      {
        id: leaderboard.tieBreaker,
        displayText:
          leaderboard.tieBreaker === "NONE"
            ? "None"
            : leaderboard.tieBreaker === "FIRST_UPDATED"
              ? "First Updated"
              : "First Entered",
      },
    ],
    scoringMode: [
      {
        id: leaderboard.scoringMode,
        displayText:
          leaderboardDataMap[leaderboard.scoringMode] ?? leaderboard.scoringMode,
      },
    ],
    activePointMinimum: leaderboard.activePointMinimum,
    minParticipationCount: leaderboard.minParticipationCount,
    awardApproval: [
      {
        id: leaderboard.awardApproval,
        displayText:
          leaderboard.awardApproval === "MANUAL"
            ? "Manual Approval"
            : "Automatic Approval",
      },
    ],
  };
};

// A function to extract the error text from the complex nested error message
export function extractErrorText(errorMessage) {
  try {
    // First, try to find the JSON part within the error message
    const jsonMatch = errorMessage.match(/\[\{.*\}\]/s);
    if (jsonMatch) {
      // Extract the JSON string and parse it
      const jsonStr = jsonMatch[0];
      // Remove the square brackets at the beginning and end
      const cleanedJson = jsonStr.substring(1, jsonStr.length - 1);
      const parsedJson = JSON.parse(cleanedJson);

      // Extract the text property from the message object
      return parsedJson.message?.text
        ? `Failed to upload ranking list file. ${parsedJson.message?.text}`
        : "Failed to upload ranking list file. Please try again.";
    }

    // Alternative approach if the above doesn't work
    // Look specifically for the "text" field pattern
    const textMatch = errorMessage.match(/"text"\s*:\s*"([^"]+)"/);
    if (textMatch && textMatch[1]) {
      return `Failed to upload ranking list file. ${textMatch[1]}`;
    }

    return "Failed to upload ranking list file. Please try again.";
  } catch (error) {
    console.error("Error parsing error message:", error);
    return "Failed to upload ranking list file. Please try again.";
  }
}
